/**
 * Отправка отчётов об ошибках на бэк (partner-webapp). Зеркало client-webapp/lib/error-report.ts.
 *
 * Дизайн (важно, не ломать):
 * - Сырой fetch, НЕ api.request()/react-query → иначе ошибка репортера снова попадёт в
 *   queryCache.subscribe (__root) → рекурсия. reportError никогда не бросает наружу.
 * - report_code генерится синхронно и показывается юзеру мгновенно; тот же код летит в БД.
 * - autoLog дедупит по fingerprint (cooldown) + глобальный лимит частоты (штормозащита).
 */

import { api, ApiError } from '@/lib/api';
import { APP_VERSION } from '@/lib/version';
import { getTgUser, getWebApp } from '@/lib/telegram';

const API_BASE = import.meta.env.VITE_API_BASE || '';
const ENDPOINT = '/api/partner/errors';

const AUTO_COOLDOWN_MS = 60_000;
const MAX_AUTO_PER_MIN = 10;

const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

export type ReportKind = 'auto' | 'user_report';
export type ReportSeverity = 'crash' | 'error';

export interface ReportResult {
  reportCode: string;
  ok: boolean;
}

export interface ReportOptions {
  kind: ReportKind;
  severity: ReportSeverity;
  description?: string;
  componentStack?: string;
}

export function generateReportCode(length = 8): string {
  const bytes = new Uint8Array(length);
  (globalThis.crypto ?? window.crypto).getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < length; i++) out += ALPHABET[bytes[i] % ALPHABET.length];
  return out;
}

function normalizeMessage(message: string): string {
  return message
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '#')
    .replace(/https?:\/\/\S+/g, '#')
    .replace(/0x[0-9a-f]+/gi, '#')
    .replace(/\d+/g, '#')
    .trim()
    .slice(0, 200);
}

function hashStr(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h.toString(16);
}

export function fingerprintOf(error: unknown): string {
  const err = error as { name?: string; message?: string; stack?: string } | null;
  const name = err?.name ?? '';
  const message = err?.message ?? String(error ?? '');
  let firstFrame = '';
  if (err?.stack) {
    for (const raw of err.stack.split('\n')) {
      const line = raw.trim();
      if (line.startsWith('at ') || line.includes('@') || line.includes('(')) {
        firstFrame = line;
        break;
      }
    }
  }
  return hashStr(`${name}|${normalizeMessage(message)}|${firstFrame}`);
}

function collectContext() {
  const tg = getWebApp();
  return {
    viewport: { w: window.innerWidth, h: window.innerHeight },
    tg_platform: tg?.platform,
    tg_version: tg?.version,
    color_scheme: tg?.colorScheme,
    // Загрузился ли SDK и пришла ли initData. Без этих двух флагов отказ «Telegram не
    // отвечает» неотличим от обычной ошибки: tg_user_id в таком отчёте пустой.
    tg_sdk_loaded: Boolean(tg),
    has_init_data: Boolean(tg?.initData),
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
  };
}

interface ErrorReportBody {
  report_code: string;
  kind: ReportKind;
  severity: ReportSeverity;
  message: string;
  error_name?: string;
  technical?: string;
  stack?: string;
  component_stack?: string;
  route: string;
  app_version: string;
  user_agent: string;
  tg_user_id?: number;
  context: Record<string, unknown>;
  user_description?: string;
  fingerprint: string;
}

function buildBody(error: unknown, opts: ReportOptions, reportCode: string): ErrorReportBody {
  const err = error as { name?: string; message?: string; stack?: string } | null;
  const isApi = error instanceof ApiError;
  return {
    report_code: reportCode,
    kind: opts.kind,
    severity: opts.severity,
    message: (err?.message ?? String(error ?? 'Unknown error')).slice(0, 4000),
    error_name: err?.name,
    technical: isApi ? (error as ApiError).technical : undefined,
    stack: err?.stack?.slice(0, 20000),
    component_stack: opts.componentStack?.slice(0, 20000),
    route: window.location.pathname + window.location.search,
    app_version: APP_VERSION,
    user_agent: navigator.userAgent.slice(0, 512),
    tg_user_id: getTgUser()?.id,
    context: collectContext(),
    user_description: opts.description?.slice(0, 4000) || undefined,
    fingerprint: fingerprintOf(error),
  };
}

export async function reportError(error: unknown, opts: ReportOptions): Promise<ReportResult> {
  const reportCode = generateReportCode();
  try {
    const body = buildBody(error, opts, reportCode);
    const token = api.getAuth().token;
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(`${API_BASE}${ENDPOINT}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    });
    return { reportCode, ok: res.ok };
  } catch {
    return { reportCode, ok: false };
  }
}

const lastAutoByFp = new Map<string, number>();
let autoWindow: number[] = [];

export async function autoLog(
  error: unknown,
  opts: Omit<ReportOptions, 'kind'> = { severity: 'error' },
): Promise<string | null> {
  const now = Date.now();
  const fp = fingerprintOf(error);

  const last = lastAutoByFp.get(fp);
  if (last && now - last < AUTO_COOLDOWN_MS) return null;

  autoWindow = autoWindow.filter((t) => now - t < 60_000);
  if (autoWindow.length >= MAX_AUTO_PER_MIN) return null;

  lastAutoByFp.set(fp, now);
  autoWindow.push(now);

  const { reportCode } = await reportError(error, { ...opts, kind: 'auto' });
  return reportCode;
}
